/**
 * FRSkinMask — граница кожи ПО ЦВЕТУ, для кадров, где лица не нашлось.
 *
 * ⚠️ Это НЕ замена геометрии, и это важно понимать до чтения кода.
 * Маска «по цвету» ловит руки, шею, деревянную дверь, кирпич и бежевую стену
 * — именно поэтому основной путь ретуши задаёт границу овалом лица, а не
 * оттенком (см. шапку landmarks.js). Здесь она заводится ради одного случая:
 * лицо в кадре ЕСТЬ, человек его видит, а детектор его не берёт — снимок
 * документа, сильный профиль, маска, грим, рисунок, очень мелкое лицо. До
 * сих пор ответом было «лицо не найдено» и мёртвая страница; теперь ответ —
 * «нашёл кожу по цвету, вот результат, смотрите сами».
 *
 * Что делает честным этот компромисс:
 *
 *   1. Лейн включается ТОЛЬКО вручную и НАЗЫВАЕТ себя на странице. Молчаливой
 *      подмены геометрии цветом нет нигде.
 *   2. Работает по области, которую задал человек (`region`), если он её
 *      задал: обвёл лицо — за пределы рамки не выходим.
 *   3. Отсекаются возможности, которым нужна геометрия (зубы, белок глаз,
 *      круги под глазами) — они не «работают хуже», их просто нет.
 *
 * Метод. Порог по хроматичности в YCbCr — старый, но до сих пор лучший по
 * соотношению «цена/качество» признак кожи: он инвариантен к яркости, то есть
 * не разваливается на тени и на блике (а блик мы как раз и снимаем). Поверх
 * него — три отсечки, каждая закрывает свой ложный класс:
 *
 *   · насыщенность сверху — чистый красный/оранжевый предмет ярче любой кожи;
 *   · яркость снизу и сверху — угольная тень и выбитое в белое небо;
 *   · СВЯЗНОСТЬ — мелкая крошка по всему кадру выбрасывается, остаются
 *     крупные пятна. Это то, что отличает лицо от текстуры ковра.
 */
(function (root) {
    'use strict';

    /**
     * @param {object} pl      линейные плоскости {w,h,R,G,B}
     * @param {object} o       {region:{x,y,w,h} в долях кадра, feather, minBlob}
     * @returns {{mask:Float32Array, coverage:number, blobs:number}}
     */
    function build(pl, o) {
        o = o || {};
        var w = pl.w, h = pl.h, n = w * h;
        var raw = new Uint8Array(n);
        var lin2srgb = root.FREngine._lin2srgb;

        var rg = o.region || null;
        var rx0 = rg ? Math.max(0, Math.floor(rg.x * w)) : 0;
        var ry0 = rg ? Math.max(0, Math.floor(rg.y * h)) : 0;
        var rx1 = rg ? Math.min(w, Math.ceil((rg.x + rg.w) * w)) : w;
        var ry1 = rg ? Math.min(h, Math.ceil((rg.y + rg.h) * h)) : h;

        var hits = 0;
        for (var y = ry0; y < ry1; y++) {
            for (var x = rx0; x < rx1; x++) {
                var i = y * w + x;
                // Признак считается в sRGB: пороги YCbCr описаны для него, а
                // плоскости движка линейные. Обратный перевод дешевле, чем
                // выдумывать свои пороги для линейного света.
                var R = lin2srgb(pl.R[i]) * 255, G = lin2srgb(pl.G[i]) * 255, B = lin2srgb(pl.B[i]) * 255;
                var Y = 0.299 * R + 0.587 * G + 0.114 * B;
                if (Y < 32 || Y > 246) continue;                    // тень и выбитое
                var Cb = 128 - 0.168736 * R - 0.331264 * G + 0.5 * B;
                var Cr = 128 + 0.5 * R - 0.418688 * G - 0.081312 * B;
                if (Cb < 77 || Cb > 132) continue;
                if (Cr < 132 || Cr > 178) continue;
                // Кожа всегда R > G > B. Обратный порядок — это не кожа, каким
                // бы удачным ни вышел Cb/Cr (розовая ткань с синим отливом).
                if (!(R >= G && G >= B - 6)) continue;
                var mx = Math.max(R, G, B), mn = Math.min(R, G, B);
                var sat = mx > 0 ? (mx - mn) / mx : 0;
                if (sat > 0.62) continue;                            // сигнальный оранжевый
                raw[i] = 1; hits++;
            }
        }
        if (!hits) return { mask: new Float32Array(n), coverage: 0, blobs: 0 };

        // ── Связность: выбрасываем крошку ───────────────────────────────────
        // Порог берётся ДОЛЕЙ кадра, а не в пикселях: одно и то же лицо на
        // превью 400 px и на снимке 4000 px обязано пройти одинаково.
        var minBlob = Math.max(60, Math.round((o.minBlob === undefined ? 0.0012 : o.minBlob) * n));
        var lab = new Int32Array(n), stack = new Int32Array(n);
        var kept = 0, blobs = 0, id = 0;
        for (var s = 0; s < n; s++) {
            if (!raw[s] || lab[s]) continue;
            id++;
            var top = 0, cnt = 0;
            stack[top++] = s; lab[s] = id;
            var members = [];
            while (top) {
                var p = stack[--top];
                members.push(p); cnt++;
                var px = p % w, py = (p / w) | 0;
                if (px > 0 && raw[p - 1] && !lab[p - 1]) { lab[p - 1] = id; stack[top++] = p - 1; }
                if (px < w - 1 && raw[p + 1] && !lab[p + 1]) { lab[p + 1] = id; stack[top++] = p + 1; }
                if (py > 0 && raw[p - w] && !lab[p - w]) { lab[p - w] = id; stack[top++] = p - w; }
                if (py < h - 1 && raw[p + w] && !lab[p + w]) { lab[p + w] = id; stack[top++] = p + w; }
            }
            if (cnt < minBlob) {
                for (var m = 0; m < members.length; m++) raw[members[m]] = 0;
            } else { kept += cnt; blobs++; }
        }
        if (!kept) return { mask: new Float32Array(n), coverage: 0, blobs: 0 };

        // ── Мягкий край ─────────────────────────────────────────────────────
        // Тот же приём, что у геометрической маски: жёсткая граница даёт
        // видимый шов, а мы работаем ровно на границе.
        var soft = new Float32Array(n);
        for (var q = 0; q < n; q++) soft[q] = raw[q];
        var featherPx = Math.max(2, Math.round((o.feather === undefined ? 0.008 : o.feather) * Math.max(w, h)));
        var blurred = root.FREngine.boxBlur(soft, w, h, featherPx);
        // Ядро остаётся единицей, край плавно спадает — иначе центр щеки
        // получает 0.7 и ретушь применяется вполсилы там, где она нужнее всего.
        for (var z = 0; z < n; z++) {
            var v = raw[z] ? 1 : Math.min(1, blurred[z]);
            soft[z] = v < 0.02 ? 0 : v;
        }
        return { mask: soft, coverage: kept / n, blobs: blobs };
    }

    root.FRSkinMask = { build: build };
})(typeof window !== 'undefined' ? window : globalThis);
